import React from 'react';
import { Box } from '@mui/material';

export const SOFT_MEDIA_EDGE_FADE = {
  horizontal: { start: 9, end: 91 },
  vertical: { start: 8, end: 86 },
} as const;

export const SOFT_MEDIA_EDGE_MASK = 'linear-gradient(to right, transparent 0%, #000 9%, #000 91%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 8%, #000 86%, transparent 100%)';

interface FadeVideoProps {
  videoSrc?: string;
  webmSrc?: string;
  posterSrc?: string;
  imageSrc?: string;
  alt?: string;
  maxWidth?: string | number;
  borderRadius?: string;
  horizontalFade?: {
    start: number;
    end: number;
  };
  verticalFade?: {
    start: number;
    end: number;
  };
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
}

const FadeVideo: React.FC<FadeVideoProps> = ({
  videoSrc,
  webmSrc,
  posterSrc,
  imageSrc,
  alt = "Fade video/image",
  maxWidth = '600px',
  borderRadius = '20px',
  horizontalFade = SOFT_MEDIA_EDGE_FADE.horizontal,
  verticalFade = SOFT_MEDIA_EDGE_FADE.vertical,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true
}) => {
  const maskStyle = {
    maskImage: `
      linear-gradient(to right, transparent 0%, black ${horizontalFade.start}%, black ${horizontalFade.end}%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black ${verticalFade.start}%, black ${verticalFade.end}%, transparent 100%)
    `,
    WebkitMaskImage: `
      linear-gradient(to right, transparent 0%, black ${horizontalFade.start}%, black ${horizontalFade.end}%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, black ${verticalFade.start}%, black ${verticalFade.end}%, transparent 100%)
    `,
    maskComposite: 'intersect',
    WebkitMaskComposite: 'source-in'
  };

  const commonStyle = {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius,
    ...maskStyle
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement | HTMLImageElement>) => {
    if (posterSrc || imageSrc) {
      const target = e.target as HTMLVideoElement | HTMLImageElement;
      const img = document.createElement('img');
      img.src = posterSrc || imageSrc || '';
      img.style.width = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = borderRadius;
      img.style.maskImage = maskStyle.maskImage;
      img.style.webkitMaskImage = maskStyle.WebkitMaskImage;
      img.style.maskComposite = maskStyle.maskComposite;
      img.style.webkitMaskComposite = maskStyle.WebkitMaskComposite;
      target.parentNode?.replaceChild(img, target);
    }
  };

  return (
    <Box sx={{ 
      position: 'relative',
      width: '100%',
      maxWidth,
      margin: '0 auto',
      borderRadius,
      border: '0px',
      overflow: 'hidden',
    }}>
      {(videoSrc || webmSrc) ? (
        <video
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          poster={posterSrc}
          style={commonStyle}
          onError={handleError}
        >
          {videoSrc && <source src={videoSrc} type="video/mp4" />}
          {webmSrc && <source src={webmSrc} type="video/webm" />}
          Your browser does not support the video tag.
        </video>
      ) : imageSrc ? (
        <img
          src={imageSrc}
          alt={alt}
          style={commonStyle}
          onError={handleError}
        />
      ) : null}
    </Box>
  );
};

export default FadeVideo;
